import { TFunction } from 'i18next';
import {
  Arguments,
  Form as PaymentForm,
  Payment,
  Results,
} from 'payments-lib-types';
import {
  always,
  complement,
  cond,
  dissocPath,
  identity,
  ifElse,
  is,
  isEmpty,
  isNil,
  lensPath,
  lensProp,
  over,
  path,
  pipe,
  prop,
  replace,
  T,
  toString,
} from 'ramda';

import { sendEventToAnalyticsFn } from './analytics';
import { mailMask } from './masks';
import { AnalyticsEventName } from '@/api/analytics';
import { showTooltip } from '@/helpers/tooltip';
import { TooltipTypes } from '@/store/tooltipStore';

export type MethodFormApi2 = Pick<
  Arguments.SubmitArgs,
  'amountValue' | 'playerFieldsData'
> &
  Partial<Pick<Arguments.SubmitArgs, 'methodFieldsData'>>;

export type MethodFormApi3 = Pick<
  Arguments.SubmitArgs,
  'amountValue' | 'playerFieldsData'
> &
  Pick<Arguments.SubmitArgs, 'methodFieldsData'>;

const checkData = ifElse(isEmpty, always({}), identity);

const checkMethodFieldsData = ifElse(
  pipe(prop('methodFieldsData'), isNil),
  over(
    lensProp<{ methodFieldsData: Record<string, any> }, 'methodFieldsData'>(
      'methodFieldsData'
    ),
    always({})
  ),
  identity
);

const formatCardExpiry = (value: string) => {
  const [date, year] = value.split('/');
  return `20${year}-${date}`;
};

const formatCardSeamlessExpiryDate = ifElse(
  pipe(path(['methodFieldsData', 'seamless_expiry_date']), isNil),
  identity,
  over(lensPath(['methodFieldsData', 'seamless_expiry_date']), formatCardExpiry)
);

const formatCardExpiryDate = ifElse(
  pipe(path(['methodFieldsData', 'expiry_date']), isNil),
  identity,
  over(lensPath(['methodFieldsData', 'expiry_date']), formatCardExpiry)
);

const formatCardNumber = (data: MethodFormApi3): MethodFormApi3 => {
  const numberPath = methodFieldsPath.creditCardNumberField;
  const encCreditcardNumberPath = methodFieldsPath.creditCardNumberPath;

  const isFieldFilled = (p: string[]) =>
    complement(isNil)(path<string>(p, data));

  const numberIsFilled = isFieldFilled(numberPath);
  const encCreditcardNumberIsFilled = isFieldFilled(encCreditcardNumberPath);

  const conditions: Array<
    [
      (data: MethodFormApi3) => boolean,
      (data: MethodFormApi3) => MethodFormApi3,
    ]
  > = [
    [() => numberIsFilled, over(lensPath(numberPath), mailMask)],
    [
      () => encCreditcardNumberIsFilled,
      over(lensPath(encCreditcardNumberPath), mailMask),
    ],
    [T, identity],
  ];

  return cond(conditions)(data);
};

const toNumber = (value: string | number) => +value;

const toStringMaybeString = ifElse(is(Number), toString, identity);

const formatAmountF = pipe(toStringMaybeString, replace(/,/g, ''), toNumber);

const formatAmount = ifElse(
  pipe(path(['amountValue']), isNil),
  identity,
  over(lensProp('amountValue'), formatAmountF)
);
export const formattingData = pipe(
  checkData,
  checkMethodFieldsData,
  formatAmount,
  formatCardExpiryDate,
  formatCardSeamlessExpiryDate,
  formatCardNumber
);

export const changeFieldNamesForMethods = (fields: PaymentForm.MethodField[]) =>
  fields.map(
    ({ fieldName, ...m }) =>
      ({
        fieldName: `methodFieldsData.${fieldName}`,
        ...m,
      }) as PaymentForm.MethodField
  );

enum PaymentsError {
  DeclinedOtherReason = 'ERR_DECLINED_OTHER_REASON',
  Declined3DAuthUserFail = 'ERR_DECLINED_3D_AUTH_USER_FAIL',
  CancelledByUser = 'ERR_CANCELLED_BY_USER',
  CancelledTimeout = 'ERR_CANCELLED_TIMEOUT',
  DeclinedExpired = 'ERR_DECLINED_EXPIRED',
  DeclinedNoFunds = 'ERR_DECLINED_NO_FUNDS',
  DeclinedBadRequest = 'ERR_DECLINED_BAD_REQUEST',
  DeclinedConfigError = 'ERR_DECLINED_CONFIG_ERROR',
  Declined3DAuthTechFail = 'ERR_DECLINED_3D_AUTH_TECH_FAIL',
  Declined3DValidationFailed = 'ERR_DECLINED_3D_VALIDATION_FAILED',
  CancelledByPSP = 'ERR_CANCELLED_BY_PSP',
  InvalidResponse = 'ERR_INVALID_RESPONSE',
  DeclinedFraud = 'ERR_DECLINED_FRAUD',
  Declined3DAuthRejected = 'ERR_DECLINED_3D_AUTH_REJECTED',
  SystemError = 'ERR_SYSTEM_ERROR',
  DeclinedInvalidAccountNumber = 'ERR_DECLINED_INVALID_ACCOUNT_NUMBER',
  NotSupported = 'ERR_NOT_SUPPORTED',
  DeclinedProviderAccountConfigError = 'ERR_DECLINED_PROVIDER_ACCOUNT_CONFIG_ERROR',
  DeclinedCurrencyNotSupported = 'ERR_DECLINED_CURRENCY_NOT_SUPPORTED',
  DeclinedBankRefusal = 'ERR_DECLINED_BANK_REFUSAL',
  PSPOutOfService = 'ERR_PSP_OUT_OF_SERVICE',
}

export const paymentsErrors = Object.values(PaymentsError);

export const mapPaymentsErrorTransl: { [key: string]: string } = {
  [PaymentsError.DeclinedOtherReason]: 'declinedOtherReason',
  [PaymentsError.Declined3DAuthUserFail]: 'declined3DAuthUserFail',
  [PaymentsError.CancelledByUser]: 'cancelledByUser',
  [PaymentsError.CancelledTimeout]: 'cancelledTimeout',
  [PaymentsError.DeclinedExpired]: 'declinedExpired',
  [PaymentsError.DeclinedNoFunds]: 'declinedNoFunds',
  [PaymentsError.DeclinedBadRequest]: 'declinedBadRequest',
  [PaymentsError.DeclinedConfigError]: 'declinedConfigError',
  [PaymentsError.Declined3DAuthTechFail]: 'declined3DAuthTechFail',
  [PaymentsError.Declined3DValidationFailed]: 'declined3DValidationFailed',
  [PaymentsError.CancelledByPSP]: 'cancelledByPSP',
  [PaymentsError.InvalidResponse]: 'invalidResponse',
  [PaymentsError.DeclinedFraud]: 'declinedFraud',
  [PaymentsError.Declined3DAuthRejected]: 'declined3DAuthRejected',
  [PaymentsError.SystemError]: 'systemError',
  [PaymentsError.DeclinedInvalidAccountNumber]: 'declinedInvalidAccountNumber',
  [PaymentsError.NotSupported]: 'notSupported',
  [PaymentsError.DeclinedProviderAccountConfigError]:
    'declinedProviderAccountConfigError',
  [PaymentsError.DeclinedCurrencyNotSupported]: 'declinedCurrencyNotSupported',
  [PaymentsError.DeclinedBankRefusal]: 'declinedBankRefusal',
  [PaymentsError.PSPOutOfService]: 'pSPOutOfService',
};

export const getDetailedPaymentsErrorText = (
  responseSubmitFormPayment: any,
  t: TFunction,
  currency?: string
) => {
  const statusCode = responseSubmitFormPayment?.statusCode;

  const errorText = (() => {
    const isResponseWitnErrorText =
      responseSubmitFormPayment?.amountFieldErrors?.length ||
      responseSubmitFormPayment?.commonErrors?.length;

    if (isResponseWitnErrorText) {
      return responseSubmitFormPayment?.amountFieldErrors?.length
        ? responseSubmitFormPayment.amountFieldErrors.join(', ')
        : responseSubmitFormPayment.commonErrors.join(', ');
    }

    if (statusCode && paymentsErrors.includes(statusCode)) {
      return t(
        `deposits:paymentsErrors.
					${mapPaymentsErrorTransl[statusCode as string]}
				`,
        statusCode === PaymentsError.DeclinedCurrencyNotSupported
          ? { currency }
          : undefined
      );
    }

    return t('deposits:paymentsErrors.declinedOtherReason');
  })();

  showTooltip({
    type: TooltipTypes.ERROR,
    body: errorText,
    removeTimeout: 10000,
  });

  sendEventToAnalyticsFn({
    name: AnalyticsEventName.MODAL__DEPOSIT__PAYMENT_FORM__ERROR_TEXT,
    attributes: {
      additionalData: JSON.stringify({
        status_code: statusCode || null,
        error_text: errorText,
      }),
    },
  });
};

export const getCardLastNumbers = (method: Payment.SavedProfile) =>
  method.title?.slice(-4);

export const methodFieldsPath = {
  cvvPath: ['methodFieldsData', 'encCvv'],
  cvvField: ['methodFieldsData', 'cvv'],
  creditCardNumberPath: ['methodFieldsData', 'encCreditcardNumber'],
  creditCardNumberField: ['methodFieldsData', 'number'],
  amountValue: ['amountValue'],
};

type SensitiveData = {
  fieldPath: string[];
  replacer?: (arg: any) => void;
  toRemove?: boolean;
}[];

export const hideSensitiveData =
  (sensitiveData: SensitiveData) => (data: { [key: string]: any }) => {
    return sensitiveData.reduce(
      (updatedData, { fieldPath, toRemove, replacer }) => {
        if (path(fieldPath, updatedData)) {
          if (toRemove) {
            updatedData = dissocPath(fieldPath, updatedData);
          }

          if (replacer) {
            const fieldLens = lensPath(fieldPath);
            updatedData = over(fieldLens, replacer, updatedData);
          }
        }
        return updatedData;
      },
      { ...data }
    );
  };

export type MethodFields = Omit<Results.GetMethodFieldsResult, 'methodFields'> &
  Partial<Pick<Results.GetMethodFieldsResult, 'methodFields'>>;

export interface IMethodFormProps extends MethodFields {
  id: string;
  currency: string;
  savedProfileDetails?: Payment.SavedProfile;
  isPrevUsed?: boolean;
}

export interface IDepositMethodFormProps extends IMethodFormProps {
  type: string;
  brand: string;
  provider: string;
}

export enum FieldsType {
  TEXT = 'text',
  FORM = 'form',
  CARD_DETAILS = 'card-details',
}
